import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { apiService } from '../services/apiService';
import { QrCode, Sparkles, X, ShieldCheck } from 'lucide-react';

export const CustomerWelcomeModal: React.FC = () => {
  const {
    isCustomerWelcomeOpen,
    setIsCustomerWelcomeOpen,
    currentTable,
    customerProfile,
    setCustomerProfile
  } = useApp();

  const { t } = useLanguage();

  const [name, setName] = useState(customerProfile?.name || '');
  const [phone, setPhone] = useState(() => {
    if (customerProfile?.phone) {
      return customerProfile.phone.replace('+358', '').trim();
    }
    return '';
  });
  const [guestCount, setGuestCount] = useState<number>(customerProfile?.guest_count || 2);
  const [gdprConsent, setGdprConsent] = useState(true);

  if (!isCustomerWelcomeOpen && customerProfile) {
    return null;
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    const fullPhone = phone.trim().startsWith('+')
      ? phone.trim()
      : `+358${phone.trim().replace(/^0/, '')}`;

    const profile = {
      name: name.trim(),
      phone: fullPhone,
      guest_count: guestCount,
      gdpr_consent: gdprConsent
    };

    setCustomerProfile(profile);
    localStorage.setItem('ovenstory_customer', JSON.stringify(profile));
    setIsCustomerWelcomeOpen(false);

    try {
      await apiService.createCustomer({
        name: profile.name,
        phone: profile.phone,
        whatsapp_number: profile.phone,
        notes: `Table ${currentTable?.table_number || 1} guest (Party of ${guestCount})`
      });
    } catch (err) {
      console.warn('Customer D1 sync on welcome:', err);
    }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 110 }}>
      <div className="modal-card" style={{ maxWidth: '480px' }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '50%',
                background: 'var(--accent-copper-light)',
                color: 'var(--accent-copper)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <QrCode size={20} />
            </div>
            <div>
              <div className="modal-title">{t('welcomeTitle')}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                theovenstory.fi • Helsinki
              </div>
            </div>
          </div>
          {customerProfile && (
            <button
              className="modal-close-btn"
              onClick={() => setIsCustomerWelcomeOpen(false)}
            >
              <X size={18} />
            </button>
          )}
        </div>

        <form onSubmit={handleSave}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {/* Locked Table Banner & Capacity */}
            <div
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-md)',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>📍</span>
                <div>
                  <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
                    {t('table')} & Pöytäkapasiteetti
                  </div>
                  <div style={{ fontWeight: 800, fontSize: '1.05rem', color: 'var(--accent-copper)' }}>
                    {currentTable?.label || `Pöytä ${currentTable?.table_number || 1}`}
                    <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginLeft: '6px' }}>
                      ({currentTable?.capacity || 4} paikkaa • {currentTable?.zone || 'Sali'})
                    </span>
                  </div>
                </div>
              </div>

              <div
                style={{
                  fontSize: '0.72rem',
                  background: 'var(--accent-emerald-light)',
                  color: 'var(--accent-emerald)',
                  padding: '4px 8px',
                  borderRadius: 'var(--radius-full)',
                  fontWeight: 700
                }}
              >
                QR Lukittu
              </div>
            </div>

            {/* Diners / Guest Count Selector */}
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>👥 Kuinka monta ruokailijaa pöydässä on?</span>
                <span style={{ color: 'var(--accent-copper)', fontWeight: 800, fontSize: '0.88rem' }}>
                  {guestCount} {guestCount === 1 ? 'henkilö' : 'henkilöä'}
                </span>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '6px', marginTop: '6px' }}>
                {[1, 2, 3, 4, 5, 6].map(num => (
                  <button
                    key={num}
                    type="button"
                    className={guestCount === num ? 'btn-primary' : 'btn-secondary'}
                    style={{
                      padding: '8px 2px',
                      fontSize: '0.84rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '2px',
                      borderRadius: 'var(--radius-sm)',
                      background: guestCount === num ? 'var(--accent-copper)' : 'var(--bg-surface)'
                    }}
                    onClick={() => setGuestCount(num)}
                  >
                    <span style={{ fontSize: '13px' }}>{num === 1 ? '👤' : '👥'}</span>
                    <span style={{ fontWeight: 800 }}>{num === 6 ? '6+' : num}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Customer Name */}
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">{t('yourName')} *</label>
              <input
                type="text"
                required
                className="form-input"
                placeholder={t('namePlaceholder')}
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>

            {/* WhatsApp / Phone with Finland Flag & Code */}
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">
                <span>{t('phoneLabel')}</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: '6px' }}>
                  (WhatsApp-tilapäivitykset)
                </span>
              </label>
              <div className="phone-input-wrap">
                <div className="phone-prefix">
                  <span>🇫🇮</span>
                  <span>+358</span>
                </div>
                <input
                  type="tel"
                  placeholder="40 123 4567"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                />
              </div>
            </div>

            {/* GDPR Consent Checkbox */}
            <div className="gdpr-card">
              <input
                type="checkbox"
                id="gdpr-check"
                required
                className="gdpr-checkbox"
                checked={gdprConsent}
                onChange={e => setGdprConsent(e.target.checked)}
              />
              <label htmlFor="gdpr-check" className="gdpr-text">
                {t('gdprConsent')}
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="submit"
              className="btn-primary"
              style={{ width: '100%', padding: '14px', fontSize: '1rem' }}
            >
              <Sparkles size={18} />
              <span>{t('startBrowsing')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
